/**
 * @module features/client-device-management/components/BatchAssignModal
 * @description Responsive modal (mobile bottom sheet / desktop dialog) enabling
 * interactive checkbox multi-selection of unassigned devices to allocate in batch
 * to `{Org}` with per-device quota and conflict diagnostics.
 */
import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Search,
  TriangleAlert,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { AppError } from "@/lib/api-client";
import { BatchAssignResponse, formatDeviceTypeLabel } from "../types";
import {
  useAvailableDevices,
  useBatchAssignDevices,
} from "../api/assignmentApi";

export interface BatchAssignModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  orgName: string;
  deviceTypes: ReadonlyArray<{ type_id: string; label: string }>;
}

export function BatchAssignModal({
  open,
  onClose,
  clientId,
  orgName,
  deviceTypes,
}: BatchAssignModalProps) {
  const [searchInput, setSearchInput] = React.useState("");
  const deferredSearch = React.useDeferredValue(searchInput.trim());
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [defaultLocation, setDefaultLocation] = React.useState("");
  const [submitError, setSubmitError] = React.useState<AppError | null>(null);
  const [report, setReport] = React.useState<BatchAssignResponse | null>(null);

  const availableQuery = useAvailableDevices(
    { search: deferredSearch || undefined },
    open
  );
  const batchMutation = useBatchAssignDevices(clientId);

  const availableDevices = React.useMemo(
    () => availableQuery.data ?? [],
    [availableQuery.data]
  );

  const handleClose = (): void => {
    setSearchInput("");
    setSelectedIds(new Set());
    setDefaultLocation("");
    setSubmitError(null);
    setReport(null);
    onClose();
  };

  const toggleDevice = (deviceId: string, checked: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(deviceId);
      } else {
        next.delete(deviceId);
      }
      return next;
    });
  };

  const allVisibleSelected =
    availableDevices.length > 0 &&
    availableDevices.every((d) => selectedIds.has(d.id));

  const toggleSelectAllVisible = (checked: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      availableDevices.forEach((d) => {
        if (checked) {
          next.add(d.id);
        } else {
          next.delete(d.id);
        }
      });
      return next;
    });
  };

  const handleBatchSubmit = async (): Promise<void> => {
    if (selectedIds.size === 0) return;
    setSubmitError(null);
    setReport(null);
    try {
      const result = await batchMutation.mutateAsync({
        device_ids: Array.from(selectedIds),
        default_location: defaultLocation.trim() || undefined,
      });
      setReport(result);
      setSelectedIds(new Set());
      if (result.failed_count === 0) {
        handleClose();
      } else {
        void availableQuery.refetch();
      }
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Batch device assignment failed.")
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent sheetOnMobile className="sm:max-w-xl max-h-[90dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Batch Assign Devices to {orgName}</DialogTitle>
          <DialogDescription>
            Select multiple unassigned hardware units to allocate in one operation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto min-h-0 pr-0.5">
          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <p>{submitError.errorDetails}</p>
            </div>
          )}

          {report && (
            <div
              role="status"
              className={`rounded-xl border p-3 text-xs space-y-2 ${
                report.failed_count > 0
                  ? "border-warning/30 bg-warning-soft text-foreground"
                  : "border-success/30 bg-success-soft text-success"
              }`}
            >
              <div className="flex items-center gap-2 font-medium">
                {report.failed_count > 0 ? (
                  <TriangleAlert
                    className="h-4 w-4 text-warning shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckCircle2
                    className="h-4 w-4 text-success shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>
                  Assigned {report.assigned_count} of {report.total_requested}{" "}
                  selected devices.
                </span>
              </div>
              {report.failures.length > 0 && (
                <ul className="space-y-1 pl-6 list-disc text-[11px] text-muted-foreground">
                  {report.failures.map((f, idx) => (
                    <li key={`${f.device_id}-${idx}`}>
                      <span className="font-mono text-foreground">
                        {f.device_id}
                      </span>
                      : {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Search & Select All Bar */}
          <div className="flex items-center gap-2.5">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                inputMode="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Filter unassigned devices..."
                aria-label="Filter unassigned devices for batch assignment"
                className="pl-9 h-10 text-base sm:text-sm bg-card"
              />
            </div>

            {availableDevices.length > 0 && (
              <div className="flex items-center gap-2 px-2 shrink-0">
                <Checkbox
                  id="batch-select-all"
                  checked={allVisibleSelected}
                  onCheckedChange={toggleSelectAllVisible}
                />
                <label
                  htmlFor="batch-select-all"
                  className="text-xs font-medium text-foreground cursor-pointer select-none"
                >
                  Select all ({availableDevices.length})
                </label>
              </div>
            )}
          </div>

          {/* Multi-select List */}
          {availableQuery.isLoading ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
          ) : availableDevices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
              No unassigned devices match your filter.
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-xl border border-border/80 divide-y divide-border/60 bg-muted/15">
              {availableDevices.map((device) => {
                const checkboxId = `batch-dev-${device.id}`;
                const checked = selectedIds.has(device.id);
                return (
                  <div
                    key={device.id}
                    className={`flex items-center gap-3 px-3.5 py-2.5 transition-colors touch:min-h-[48px] ${
                      checked ? "bg-primary/5" : "hover:bg-muted/40"
                    }`}
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={checked}
                      onCheckedChange={(val) => toggleDevice(device.id, val)}
                    />
                    <label
                      htmlFor={checkboxId}
                      className="flex-1 min-w-0 cursor-pointer select-none"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-foreground truncate">
                          {device.device_name}
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground shrink-0">
                          {formatDeviceTypeLabel(device.device_type, deviceTypes)}
                        </span>
                      </div>
                      <p className="font-mono text-[11px] text-muted-foreground truncate">
                        {device.registered_device_id}
                        {device.mac_address ? ` · ${device.mac_address}` : ""}
                      </p>
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          {/* Optional Default Location */}
          <div className="space-y-1">
            <label
              htmlFor="batch-default-location"
              className="text-xs font-medium text-foreground block"
            >
              Default Deployment Location (Optional)
            </label>
            <Input
              id="batch-default-location"
              maxLength={255}
              value={defaultLocation}
              onChange={(e) => setDefaultLocation(e.target.value)}
              placeholder="e.g. Warehouse Zone B"
              className="h-10 text-base sm:text-sm bg-card"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={batchMutation.isPending}
            className="h-10 sm:h-9 touch:min-h-[44px] text-xs"
          >
            Close
          </Button>
          <Button
            type="button"
            disabled={selectedIds.size === 0 || batchMutation.isPending}
            onClick={() => void handleBatchSubmit()}
            className="h-10 sm:h-9 touch:min-h-[44px] text-xs"
          >
            {batchMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                <span>Assigning {selectedIds.size} Devices...</span>
              </>
            ) : (
              <span>
                Assign {selectedIds.size}{" "}
                {selectedIds.size === 1 ? "Device" : "Devices"}
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

